import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Building2, Users, Mail, FileText, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useApiQuery } from '../../hooks/useApiQuery';
import { formatOptionalPercent } from '../../utils/formatters';

const formatSearchHealth = (value) => {
  const formatted = formatOptionalPercent(value);
  return formatted === 'N/A' ? 'No health score' : `Health: ${formatted}`;
};

const GlobalSearch = ({ isOpen, setIsOpen }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const navigate = useNavigate();

  // Listen for keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setIsOpen]);

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const queryActive = debouncedQuery.length >= 2;
  const { data: results, loading } = useApiQuery(
    queryActive ? `/search?q=${encodeURIComponent(debouncedQuery)}` : null,
    {
      deps: [debouncedQuery],
      showErrorToast: false,
      initialData: { accounts: [], contacts: [], communications: [] },
    },
  );

  const handleSelectResult = (type, id) => {
    setIsOpen(false);
    setSearchQuery('');
    
    if (type === 'account') {
      navigate(`/app/accounts/${id}`);
    }
    // Add more navigation cases as needed
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
        onClick={() => setIsOpen(false)}
      />

      {/* Search Modal */}
      <div className="fixed top-24 left-1/2 transform -translate-x-1/2 w-full max-w-2xl z-[60] px-4">
        <div className="bg-card rounded-lg shadow-2xl border border-border overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center border-b border-border p-4">
            <Search className="h-5 w-5 text-muted-foreground mr-3" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search accounts, contacts, communications..."
              className="flex-1 outline-none text-foreground placeholder-gray-400 dark:placeholder-gray-500 bg-transparent"
              autoFocus
            />
            {loading && <Loader2 className="h-5 w-5 text-muted-foreground animate-spin mr-3" />}
            <button
              onClick={() => setIsOpen(false)}
              className="text-muted-foreground hover:text-muted-foreground dark:hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Results */}
          <div className="max-h-[500px] overflow-y-auto">
            {searchQuery.length < 2 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Type at least 2 characters to search</p>
                <p className="text-xs mt-2 text-muted-foreground">
                  Press <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded border dark:border-gray-600">Esc</kbd> to close
                </p>
              </div>
            ) : loading ? (
              <div className="p-8 text-center">
                <Loader2 className="h-8 w-8 mx-auto mb-3 text-blue-600 animate-spin" />
                <p className="text-sm text-muted-foreground">Searching...</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {/* Accounts */}
                {results.accounts && results.accounts.length > 0 && (
                  <div className="p-3">
                    <div className="flex items-center space-x-2 px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      <Building2 className="h-4 w-4" />
                      <span>Accounts ({results.accounts.length})</span>
                    </div>
                    {results.accounts.map((account) => (
                      <button
                        key={account.id}
                        onClick={() => handleSelectResult('account', account.id)}
                        className="w-full text-left px-3 py-3 hover:bg-accent rounded-lg transition-colors flex items-center justify-between group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground group-hover:text-blue-600 flex items-center space-x-2">
                              <span>{account.company_name}</span>
                              {account.external_id && (
                                <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">
                                  {account.external_id}
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {formatSearchHealth(account.health_score)}
                            </div>
                          </div>
                        </div>
                        {account.arr && (
                          <Badge className="bg-green-100 text-green-800">
                            ${(account.arr / 1000).toFixed(0)}K ARR
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Contacts */}
                {results.contacts && results.contacts.length > 0 && (
                  <div className="p-3">
                    <div className="flex items-center space-x-2 px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      <Users className="h-4 w-4" />
                      <span>Contacts ({results.contacts.length})</span>
                    </div>
                    {results.contacts.map((contact) => (
                      <button
                        key={contact.id}
                        onClick={() => handleSelectResult('contact', contact.id)}
                        className="w-full text-left px-3 py-3 hover:bg-accent rounded-lg transition-colors flex items-center justify-between group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <Users className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground group-hover:text-blue-600">
                              {contact.name}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {contact.email}
                            </div>
                          </div>
                        </div>
                        {contact.company && (
                          <span className="text-xs text-muted-foreground">{contact.company}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Communications */}
                {results.communications && results.communications.length > 0 && (
                  <div className="p-3">
                    <div className="flex items-center space-x-2 px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      <Mail className="h-4 w-4" />
                      <span>Communications ({results.communications.length})</span>
                    </div>
                    {results.communications.map((comm) => (
                      <button
                        key={comm.id}
                        onClick={() => handleSelectResult('communication', comm.id)}
                        className="w-full text-left px-3 py-3 hover:bg-accent rounded-lg transition-colors group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <Mail className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-foreground group-hover:text-blue-600 truncate">
                              {comm.subject}
                            </div>
                            <div className="text-sm text-muted-foreground truncate">
                              {comm.preview}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* No Results */}
                {results.accounts?.length === 0 && results.contacts?.length === 0 && results.communications?.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No results found for "{searchQuery}"</p>
                    <p className="text-xs mt-2 text-muted-foreground">Try different keywords</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border bg-muted px-4 py-2 flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center space-x-4">
              <span>
                <kbd className="px-2 py-1 bg-white dark:bg-gray-700 rounded border dark:border-gray-600">↑</kbd>
                <kbd className="px-2 py-1 bg-white dark:bg-gray-700 rounded border dark:border-gray-600 ml-1">↓</kbd> to navigate
              </span>
              <span>
                <kbd className="px-2 py-1 bg-white dark:bg-gray-700 rounded border dark:border-gray-600">↵</kbd> to select
              </span>
            </div>
            <span>
              <kbd className="px-2 py-1 bg-white dark:bg-gray-700 rounded border dark:border-gray-600">Esc</kbd> to close
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default GlobalSearch;
